import { routes } from '@app/constants';
import { filterOption, getLanguageOptions } from '@app/utils/helpers';
import { useCreateCustomer, useGetHatcheries, useInvite } from '@data/hooks';
import { CreateCustomerSchema, type CreateCustomerValidator } from '@data/utils/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { Language, RoleType } from '@sdk/api';
import {
  AntFlex,
  AntForm,
  AntSpace,
  AntTypography,
  Drawer,
  type DrawerProps,
  FormItem,
  Input,
  Select,
} from '@ui/components';
import { Button } from '@ui/components';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import styles from '../styles.module.css';

export const AddCustomerDrawer = ({ toggleIsOpen, ...props }: DrawerProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { Text } = AntTypography;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<CreateCustomerValidator>({
    mode: 'onBlur',
    resolver: zodResolver(CreateCustomerSchema),
  });

  const { data: hatcheries } = useGetHatcheries({
    limit: 1000, // aligned with stakeholders
    offset: 0,
  });

  const { mutateAsync: inviteUser, isPending: isInvitePending } = useInvite();
  const { mutateAsync: createCustomer, isPending: isCreateCustomerPending } = useCreateCustomer();

  const onSubmit = async (values: CreateCustomerValidator) => {
    const { email, name, hatcheryIds, externalId, language } = values;
    const customer = await createCustomer({ name, hatcheryIds, externalId });
    await inviteUser({
      email,
      customerId: customer.id,
      type: RoleType.CustomerAdmin,
      isContactPerson: true,
      language,
    });
    navigate(`/${routes.app}/${routes.customers}/${customer.id}`);
  };

  return (
    <Drawer
      title={
        <AntFlex align="center" justify="space-between">
          <Text>{t('new_customer')}</Text>
          <AntSpace>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!isValid}
              loading={isCreateCustomerPending || isInvitePending}
              onClick={handleSubmit(onSubmit)}
              style={{ minWidth: 100 }}
            >
              {t('actions.create')}
            </Button>
          </AntSpace>
        </AntFlex>
      }
      onClose={toggleIsOpen}
      {...props}
    >
      <AntForm name="createHatchery" layout="vertical">
        <Text className={styles.drawerLabel}>{t('general')}</Text>

        <FormItem
          required
          name="hatcheryIds"
          label={t('select_hatchery')}
          error={errors.hatcheryIds?.message}
        >
          <Controller
            control={control}
            name="hatcheryIds"
            render={({ field }) => (
              <Select
                showSearch
                mode="multiple"
                filterOption={filterOption}
                options={hatcheries?.items.map((h) => ({ value: h.id, label: h.name }))}
                {...field}
              />
            )}
          />
        </FormItem>

        <FormItem required name="name" label={t('customer_name')} error={errors.name?.message}>
          <Controller control={control} name="name" render={({ field }) => <Input {...field} />} />
        </FormItem>

        <FormItem name="externalId" label={t('external_id')} error={errors.externalId?.message}>
          <Controller
            control={control}
            name="externalId"
            render={({ field }) => <Input {...field} />}
          />
        </FormItem>

        <Text className={styles.drawerLabel}>{t('contact_person')}</Text>

        <FormItem
          required
          name="email"
          label={t('email_address')}
          error={errors.email?.message}
          help={t('invite.we_will_use_this_email_to_invite')}
        >
          <Controller
            control={control}
            name="email"
            render={({ field }) => <Input type="email" {...field} />}
          />
        </FormItem>

        <FormItem required name="language" label={t('language')} error={errors.language?.message}>
          <Controller
            control={control}
            name="language"
            defaultValue={i18n.language as Language}
            render={({ field }) => <Select options={getLanguageOptions()} {...field} />}
          />
        </FormItem>
      </AntForm>
    </Drawer>
  );
};
